import { useEffect, useMemo, useState } from 'react';
import { Download, Share, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const DISMISS_KEY = 'fieldcrm-install-prompt-dismissed-at';
const DAY_MS = 24 * 60 * 60 * 1000;

function readDismissedAt() {
  try {
    return Number(window.sessionStorage?.getItem(DISMISS_KEY) || 0);
  } catch {
    return 0;
  }
}

function rememberDismissedAt() {
  try {
    window.sessionStorage?.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    return false;
  }
}

function isStandalone() {
  return window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;
}

function isIOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent || '');
}

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [installed, setInstalled] = useState(false);
  const ios = useMemo(() => isIOS(), []);

  useEffect(() => {
    if (isStandalone()) return;
    const dismissedAt = readDismissedAt();
    if (Date.now() - dismissedAt < 7 * DAY_MS) return;

    const beforeInstall = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
      setShow(true);
    };
    const appInstalled = () => {
      setInstalled(true);
      setShow(false);
      setDeferredPrompt(null);
    };
    window.addEventListener('beforeinstallprompt', beforeInstall);
    window.addEventListener('appinstalled', appInstalled);

    const timer = window.setTimeout(() => {
      if (ios && !isStandalone()) setShow(true);
    }, 1200);
    return () => {
      window.removeEventListener('beforeinstallprompt', beforeInstall);
      window.removeEventListener('appinstalled', appInstalled);
      window.clearTimeout(timer);
    };
  }, [ios]);

  const dismiss = () => {
    rememberDismissedAt();
    setShow(false);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice.catch(() => null);
    setDeferredPrompt(null);
    setShow(false);
  };

  if (installed || !show) return null;

  return (
    <div className="fixed inset-x-3 bottom-4 z-[80] mx-auto max-w-md rounded-md border border-zinc-200 bg-zinc-950 p-3 text-white shadow-2xl sm:bottom-5" data-testid="pwa-install-prompt">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-zinc-950">
          <img src="/bcjr-logo.png" alt="Butler County Junk Removal" className="h-9 w-9 rounded-full object-contain" data-testid="pwa-install-logo" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-sm font-bold" data-testid="pwa-install-title">Install FieldCRM</p>
              <p className="mt-0.5 text-xs text-zinc-300" data-testid="pwa-install-description">
                Add the mobile estimator to your home screen for faster field pricing.
              </p>
            </div>
            <button type="button" onClick={dismiss} className="rounded p-1 text-zinc-400 hover:bg-white/10 hover:text-white" data-testid="pwa-install-dismiss-button" aria-label="Dismiss install prompt">
              <X className="h-4 w-4" />
            </button>
          </div>
          {ios && !deferredPrompt ? (
            <div className="mt-3 rounded bg-white/10 p-2 text-xs text-zinc-200" data-testid="pwa-install-ios-instructions">
              Tap <Share className="mx-1 inline h-3.5 w-3.5" /> Share, then choose <span className="font-semibold text-white">Add to Home Screen</span>.
            </div>
          ) : (
            <Button type="button" onClick={install} className="mt-3 h-10 w-full bg-emerald-500 text-zinc-950 hover:bg-emerald-400" data-testid="pwa-install-button" disabled={!deferredPrompt}>
              <Download className="mr-2 h-4 w-4" /> Install app
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}